import {
  AG_GRID_ASSET_FILTERS,
  AG_GRID_ASSET_STATE,
  AG_GRID_INITIATIVE_FILTERS,
  AG_GRID_INITIATIVE_STATE,
  AG_GRID_INVESTMENT_FILTERS,
  AG_GRID_INVESTMENT_STATE,
  AG_GRID_SITES_FILTERS,
  AG_GRID_SITES_STATE,
  ASSET_FILTER_KEY_PREFIX,
  INITIATIVE_FILTER_KEY_PREFIX,
  INVEST_FILTER_KEY_PREFIX,
  PRICESHEETS_FILTER_KEY_PREFIX,
  SITES_FILTER_KEY_PREFIX
} from './../../../@core/utils/constants';
import { ActivatedRoute } from '@angular/router';
import { numberColumns, numberVariableColumns, SpreadsheetColumnsService, TableFilterColumns } from '../../../@core/utils/spreadsheet-columns.service';
import { Component, EventEmitter, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { GridApi, GridState } from 'ag-grid-community';

import { Subscription, combineLatest, Observable } from 'rxjs';
import { QuickFiltersService } from '../../../@core/utils';
import { KPI } from '../../../@core/api';
import { map } from 'rxjs/operators';

@Component({
    selector: 'app-ag-grid-base',
    template: '',
    styleUrls: [],
    standalone: true,
})
export class AgGridBaseComponent implements OnInit, OnDestroy, OnChanges {
  constructor(
    protected baseSpreadsheetColumnsService: SpreadsheetColumnsService,
    protected quickFiltersService: QuickFiltersService,
    protected route: ActivatedRoute,
  ) { }

  @Output() protected quickFiltersReady = new EventEmitter<boolean>();

  protected subscriptions: Subscription[] = [];

  public gridApi: GridApi;
  public initialState?: GridState;
  protected spreadsheetType: KPI.SpreadsheetType;

  protected firstColIndexBySpreadsheetType = {
    'investment': 4,
    'asset': 1,
    'initiative': 5,
    'site': 1,
  }

  protected filterKeyPrefixMap = {
    'investment': INVEST_FILTER_KEY_PREFIX,
    'asset': ASSET_FILTER_KEY_PREFIX,
    'initiative': INITIATIVE_FILTER_KEY_PREFIX,
    'site': SITES_FILTER_KEY_PREFIX,
    'pricesheet': PRICESHEETS_FILTER_KEY_PREFIX,
  }

  protected filtersLocalStorageKeyMap = {
    'investment': AG_GRID_INVESTMENT_FILTERS,
    'asset': AG_GRID_ASSET_FILTERS,
    'initiative': AG_GRID_INITIATIVE_FILTERS,
    'site': AG_GRID_SITES_FILTERS,
  }

  protected stateLocalStorageKeyMap = {
    'investment': AG_GRID_INVESTMENT_STATE,
    'asset': AG_GRID_ASSET_STATE,
    'initiative': AG_GRID_INITIATIVE_STATE,
    'site': AG_GRID_SITES_STATE,
  }

  protected isQuickFiltered: boolean = false;
  protected urlParamsApplied: boolean = false;
  protected inputFiltersApplied: boolean = false;

  public ngOnInit() { }

  public ngOnChanges(_changes) { }

  protected applyQuickFilters(): Observable<TableFilterColumns[]> {
    return combineLatest([
      this.quickFiltersService.getFilters(this.spreadsheetType, true),
      this.route.queryParams,
    ]).pipe(
      map(([filters, params]) => {
        const colsThatNeedFilterValues: TableFilterColumns[] = [];
        const filterKeys = Object.keys(params).some((key) =>
          key.startsWith(this.filterKeyPrefixMap[this.spreadsheetType]),
        );
        if (!filterKeys || this.urlParamsApplied) {
          Object.keys(filters).map((column: TableFilterColumns) => {
            // No filter, make sure ag-grid's filter is destroyed
            if (!filters[column] || !filters[column].length) {
              this.gridApi.destroyFilter(column);
              const colDef = this.gridApi?.getColumnDef(column);
              if (!colDef?.lockPosition) {
                this.gridApi?.setColumnsVisible([column], false);
              }
            } else {
              this.gridApi?.getColumnFilterInstance(column).then((filterInstance) => {
                const values = Array.isArray(filters[column])
                  ? filters[column].map((v) => v !== null ? v.toString() : null)
                  : filters[column];
                // Manage number column filters
                if (
                  (numberColumns.includes(column) || numberVariableColumns.some((c) => column.includes(c)))
                  && values
                ) {
                  const model = this.quickFiltersService.buildNumberFilter(values);
                  if (model) {
                    filterInstance?.setModel(model);
                  }
                } else if (filterInstance) {
                  // Manage all other filters (data sets)
                  filterInstance?.setModel({
                    filterType: 'set',
                    values,
                  });
                }
                const colDef = this.gridApi.getColumnDef(column);
                const isLocked = colDef?.lockPosition;

                if (!isLocked) {
                  const columns = this.gridApi?.getColumnState();
                  let targetIndex: number = this.firstColIndexBySpreadsheetType[this.spreadsheetType];
                  let targetIndexFound: boolean = false;
                  columns?.forEach((_col, i) => {
                    if (!isLocked && !targetIndexFound) {
                      targetIndex = i;
                      targetIndexFound = true;
                    }
                  });
                  this.gridApi?.moveColumns([column], targetIndex);
                  this.gridApi?.setColumnsVisible([column], true);
                  // If initiative category is filtered, move initiative type column to the right of initiative category
                  if (column === 'initiative_type_1') {
                    this.gridApi?.moveColumns(['initiative_type_2'], targetIndex + 1);
                    this.gridApi?.setColumnsVisible(['initiative_type_2'], true);
                  }
                }
                colsThatNeedFilterValues.push(column);
              });
            }
          });
        }
        return colsThatNeedFilterValues;
      }),
    );
  }

  protected isUrlParamsFilters(): Observable<boolean> {
    return this.route.queryParams.pipe(
      map((params) => {
        // Retrieve the filter keys from URL parameters
        const filterKeys = Object.keys(params).filter((key) =>
          key.startsWith(this.filterKeyPrefixMap[this.spreadsheetType]),
        );
        return !!filterKeys?.length;
      }),
    );
  }

  protected applyCacheFiltersAndSorts() {
    // Retrieve filters model from cache
    const filterModelJSON = localStorage.getItem(this.filtersLocalStorageKeyMap[this.spreadsheetType]);
    if (filterModelJSON) {
      // Apply filters model to the grid
      const filterModel: { [key: string]: any } = JSON.parse(filterModelJSON);
      if (filterModel && Object.keys(filterModel).length) {
        this.gridApi.setFilterModel(filterModel);
      } else {
        this.gridApi.setFilterModel(null);
      }
      return filterModel;
    }
    return {};
  }

  protected getCachedFilterModel() {
    const filterModelJSON = localStorage.getItem(this.filtersLocalStorageKeyMap[this.spreadsheetType]);
    if (filterModelJSON) {
      try {
        const filterModel: { [key: string]: any } = JSON.parse(filterModelJSON);
        return filterModel || {};
      } catch (e) {
        console.error('Failed to parse cached filter model:', e);
        return {};
      }
    }
    return {};
  }

  protected applyCachedFilterModel(filterModel: any) {
    if (filterModel && Object.keys(filterModel).length) {
      this.gridApi.setFilterModel(filterModel);
    }
  }

  protected saveState() {
    const state = this.gridApi?.getColumnState();
    const groupState = this.gridApi?.getColumnGroupState();
    const combinedState = {
      columnState: state,
      groupState: groupState
    };
    if (state?.length || groupState?.length) {
      localStorage.setItem(this.stateLocalStorageKeyMap[this.spreadsheetType], JSON.stringify(combinedState));
    }
  }

  protected alwaysHiddenColumns: string[] = [];

  protected restoreState() {
    const savedState = JSON.parse(localStorage.getItem(this.stateLocalStorageKeyMap[this.spreadsheetType]));
    if (savedState) {
      const applyState = (state: any[]) =>
        this.alwaysHiddenColumns.length
          ? state.map(col => this.alwaysHiddenColumns.includes(col.colId) ? { ...col, hide: true } : col)
          : state;
      if (savedState.columnState && savedState.groupState) {
        this.gridApi?.applyColumnState({
          state: applyState(savedState.columnState),
          applyOrder: true,
        });
        this.gridApi?.setColumnGroupState(savedState.groupState);
      } else if (Array.isArray(savedState)) {
        this.gridApi?.applyColumnState({
          state: applyState(savedState),
          applyOrder: true,
        });
      }
    }
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
